import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../../services/api';
import LanguageSelector from '../../components/LanguageSelector';
import { passwordRulesPass, PasswordChecklist } from '../../components/PasswordRules';
import useBrand from '../../styles/theme/brands/useBrand';

const PasswordResetConfirm = () => {
  // Login e redefinição de senha mostram sempre a marca Beworks.
  const { logo: beworksLogo } = useBrand('beworks');
  const { t } = useTranslation();
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showNew1, setShowNew1] = useState(false);
  const [showNew2, setShowNew2] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [invalidLink, setInvalidLink] = useState(false);

  const allRulesPass = useMemo(() => passwordRulesPass(newPassword1), [newPassword1]);
  const mismatch = newPassword2.length > 0 && newPassword2 !== newPassword1;

  const resetConfirmMutation = useMutation({
    mutationFn: () => authService.passwordResetConfirm(uid, token, newPassword1, newPassword2),
    onSuccess: () => {
      setTimeout(() => navigate('/login'), 3000);
    },
    onError: (error) => {
      const data = error?.response?.data;
      if (data?.uid || data?.token) {
        setInvalidLink(true);
        return;
      }
      if (data?.detail) {
        setFieldErrors({ non_field_errors: [data.detail] });
      } else if (data && typeof data === 'object') {
        setFieldErrors(data);
      } else {
        setFieldErrors({ non_field_errors: [t('passwordResetConfirm.error')] });
      }
    },
  });

  const firstError = (key) => fieldErrors[key]?.[0] || null;

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    resetConfirmMutation.mutate();
  };

  const isPending = resetConfirmMutation.isPending;
  const isSuccess = resetConfirmMutation.isSuccess;
  const canSubmit = allRulesPass && newPassword2 && !mismatch;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
        position: 'relative',
        px: 2,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 20 },
          right: { xs: 16, sm: 20 },
        }}
      >
        <LanguageSelector />
      </Box>

      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Box sx={{ width: 220, height: 43, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <img src={beworksLogo} alt="Beworks" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </Box>

          <Typography component="h1" variant="h5" sx={{ mb: 1, color: 'text.primary', textAlign: 'center' }}>
            {t('passwordResetConfirm.title')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
            {t('passwordResetConfirm.subtitle')}
          </Typography>

          {invalidLink ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('passwordResetConfirm.invalidLink')}
              </Alert>
              <Box sx={{ textAlign: 'center' }}>
                <Link to="/password-reset" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Typography variant="body2" sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                    {t('passwordResetConfirm.requestNewLink')}
                  </Typography>
                </Link>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              {firstError('non_field_errors') && (
                <Alert severity="error" sx={{ mb: 2 }}>{firstError('non_field_errors')}</Alert>
              )}

              {isSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('passwordResetConfirm.success')}
                </Alert>
              )}

              <Box sx={{ mb: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  type={showNew1 ? 'text' : 'password'}
                  label={t('passwordResetConfirm.newPassword')}
                  value={newPassword1}
                  onChange={(e) => {
                    setNewPassword1(e.target.value);
                    clearFieldError('new_password1');
                  }}
                  error={!!firstError('new_password1')}
                  helperText={firstError('new_password1')}
                  disabled={isPending || isSuccess}
                  autoComplete="new-password"
                  autoFocus
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowNew1((v) => !v)} edge="end">
                          {showNew1 ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <PasswordChecklist password={newPassword1} t={t} />
              </Box>

              <TextField
                margin="normal"
                required
                fullWidth
                type={showNew2 ? 'text' : 'password'}
                label={t('passwordResetConfirm.confirmPassword')}
                value={newPassword2}
                onChange={(e) => {
                  setNewPassword2(e.target.value);
                  clearFieldError('new_password2');
                }}
                error={!!firstError('new_password2') || mismatch}
                helperText={
                  firstError('new_password2')
                    || (mismatch ? t('passwordResetConfirm.mismatch') : t('passwordResetConfirm.confirmPasswordHint'))
                }
                disabled={isPending || isSuccess}
                autoComplete="new-password"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNew2((v) => !v)} edge="end">
                        {showNew2 ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isPending || isSuccess || !canSubmit}
                size="large"
                sx={{ py: 1.5, mb: 2 }}
              >
                {isPending ? <CircularProgress size={24} color="inherit" /> : t('passwordResetConfirm.submitButton')}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PasswordResetConfirm;
